// The times, durations and credits the agent screens say in words (agent
// redesign 2026-10-09): "next run tomorrow at 07:00", a run row's "Friday,
// 07:00" and "3 min · 3.1 credits", a run card's "Monday 12 October, 07:00 …
// Took 3 min 4 s".
// Callers supply the interface locale and catalog; standalone English callers keep
// British date formatting and a 24-hour clock.

import { formatters } from '@/usage/format';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';

const DATES = 'en-GB';
type RuntimeCopy = Readonly<Record<keyof typeof runtimeCopy.en, string>>;
const DAY_MS = 86_400_000;

const format = (date: Date, options: Intl.DateTimeFormatOptions, locale = DATES) =>
  new Intl.DateTimeFormat(locale, options).format(date);
const clock = (date: Date, locale = DATES) =>
  format(date, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, locale);
const weekday = (date: Date, locale = DATES) => format(date, { weekday: 'long' }, locale);

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
/** Whole calendar days from `now` to `date`: 0 today, 1 tomorrow, -1 yesterday. */
const daysFrom = (date: Date, now: Date) =>
  Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);

/** "5 Oct", with the year when it is not this year's. */
function dayMonth(date: Date, now: Date, locale = DATES) {
  return format(
    date,
    {
      day: 'numeric',
      month: 'short',
      ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    },
    locale,
  );
}

/** When the next run starts: "today at 07:00", "tomorrow at 07:00", "on Monday at 07:00". */
export function nextRunPhrase(
  value: string,
  now = new Date(),
  locale = DATES,
  copy: RuntimeCopy = runtimeCopy.en,
) {
  const date = new Date(value);
  const days = daysFrom(date, now);
  const at = `${copy.at} ${clock(date, locale)}`;
  if (days <= 0) return `${copy.today} ${at}`;
  if (days === 1) return `${copy.tomorrow} ${at}`;
  if (days < 7) return `${copy.on} ${weekday(date, locale)} ${at}`;
  return `${copy.on} ${dayMonth(date, now, locale)} ${at}`;
}

/** A run row's time: "Today, 07:00", "Yesterday, 07:00", "Friday, 07:00", "5 Oct, 16:40". */
export function runWhen(
  value: string | null | undefined,
  now = new Date(),
  locale = DATES,
  copy: RuntimeCopy = runtimeCopy.en,
) {
  if (!value) return '';
  const date = new Date(value);
  const days = daysFrom(date, now);
  const day =
    days === 0
      ? copy.today
      : days === -1
        ? copy.yesterday
        : days < 0 && days > -7
          ? weekday(date, locale)
          : dayMonth(date, now, locale);
  return `${day}, ${clock(date, locale)}`;
}

/** A run card's time: "Monday 12 October, 07:00". */
export function runDate(value: string, locale = DATES) {
  const date = new Date(value);
  return `${format(date, { weekday: 'long', day: 'numeric', month: 'long' }, locale).replace(',', '')}, ${clock(date, locale)}`;
}

/** The last run on a list row: "today 07:03", "yesterday 07:03", "Thursday", "5 Oct". */
export function lastRunDay(
  value: string,
  now = new Date(),
  locale = DATES,
  copy: RuntimeCopy = runtimeCopy.en,
) {
  const date = new Date(value);
  const days = daysFrom(date, now);
  if (days === 0) return `${copy.today} ${clock(date, locale)}`;
  if (days === -1) return `${copy.yesterday} ${clock(date, locale)}`;
  if (days < 0 && days > -7) return weekday(date, locale);
  return dayMonth(date, now, locale);
}

/** A run row's duration: "40 s", "3 min", "1 h 5 min". */
export function durationShort(ms: number, locale = DATES) {
  const seconds = Math.max(1, Math.round(ms / 1000));
  const unit = (value: number, unitName: Intl.RelativeTimeFormatUnit) =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: unitName, unitDisplay: 'short' }).format(
      value,
    );
  if (seconds < 60) return unit(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return unit(minutes, 'minute');
  return `${unit(Math.floor(minutes / 60), 'hour')} ${unit(minutes % 60, 'minute')}`;
}

/** A run card's duration: "40 s", "3 min 4 s", "1 h 5 min". */
export function durationLong(ms: number, locale = DATES) {
  const seconds = Math.max(1, Math.round(ms / 1000));
  const unit = (value: number, unitName: Intl.RelativeTimeFormatUnit) =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: unitName, unitDisplay: 'short' }).format(
      value,
    );
  if (seconds < 60) return unit(seconds, 'second');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
    return seconds % 60
      ? `${unit(minutes, 'minute')} ${unit(seconds % 60, 'second')}`
      : unit(minutes, 'minute');
  return `${unit(Math.floor(minutes / 60), 'hour')} ${unit(minutes % 60, 'minute')}`;
}

/** The credits a run used, as Settings › Usage writes them: "3.1 credits", "1 credit",
 * "under 0.1 credits". Never a provider cost. */
export function creditsText(credits: number, locale = DATES, copy: RuntimeCopy = runtimeCopy.en) {
  if (credits > 0 && credits < 0.05) return copy.underCredits;
  const amount = formatters(locale).amount(credits);
  return `${amount} ${amount === '1' ? copy.credit : copy.credits}`;
}
